import { getEnv } from '@/lib/env'
import { tokenStore } from './tokenStore'

/**
 * MÓDULO CANÓNICO DE SESIÓN (2/3) — la renovación del access token.
 *
 * Contrato del ecosistema: `docs-ecosistema` → modelo-de-auth, «Ciclo de vida
 * del token en las webs». Fichero y API IDÉNTICOS en todas las webs SSO
 * (espejo de `tickets-web/src/auth/renovacion.ts`).
 *
 * El access token corto se renueva contra la cookie httpOnly `refreshToken` que
 * el gateway rota y comparte en `.tecopos.com` (mutation `refreshToken`: lee la
 * cookie, la re-siembra y devuelve `{ accessToken }`). Este módulo es la ÚNICA
 * pieza que habla con esa mutation.
 *
 * Se usa `fetch` directo (no `apolloClient.mutate`) para romper la dependencia
 * circular con el cliente y para bypasear el authLink/errorLink: esta llamada NO
 * debe llevar Bearer expirado ni recursar si vuelve a fallar.
 *
 * LO QUE ESTE MÓDULO ARREGLA (defecto #7 de la auditoría de sesión-viva): antes
 * la renovación devolvía `string | null` y el `null` significaba «sesión
 * muerta» — así que un gateway con 502, o un móvil que pierde la señal dos
 * segundos, DESLOGUEABA. En conectividad cubana ese es el caso común, no el
 * borde. Por eso hay TRES desenlaces y no dos, y solo UNO de ellos toca la
 * sesión.
 */

export type DesenlaceRenovacion =
  /** Hay token nuevo (ya guardado en el store): se reintenta la operación. */
  | { tipo: 'renovado'; token: string }
  /**
   * El servidor RECHAZÓ explícitamente la renovación (la mutation respondió con
   * error, o el cuerpo vino sin `accessToken`). Es el ÚNICO desenlace que
   * termina la sesión — quien lo reciba limpia y deriva al login.
   */
  | { tipo: 'sesion-invalida'; motivo: string }
  /**
   * No hubo respuesta utilizable: `fetch` que rechaza, timeout, o `res.ok ===
   * false` (502/504/500…). La sesión NO se toca; el error se propaga a la
   * operación original para que la UI lo muestre como fallo de conexión.
   */
  | { tipo: 'red'; intentos: number; motivo: string }

/** Un solo intento no distingue cuántos van; el agregado lo pone `renovarAccessToken`. */
type DesenlaceIntento =
  | { tipo: 'renovado'; token: string }
  | { tipo: 'sesion-invalida'; motivo: string }
  | { tipo: 'red'; motivo: string }

// Política de reintentos SOLO para el desenlace `red`. La variación aleatoria no
// es cosmética: la cookie es compartida en `.tecopos.com`, así que tras un
// apagón todas las pestañas del ecosistema reconectan a la vez y sin variación
// golpearían al gateway al unísono.
export const POLITICA_RED = {
  intentos: 3,
  esperaBaseMs: 400,
  factor: 2,
  /** ±30 % sobre la espera calculada. */
  variacion: 0.3,
  esperaMaximaMs: 5000,
  /** Un fetch colgado ni resuelve ni rechaza (raíz §6.7): se corta. */
  tiempoLimiteMs: 8000,
} as const

/**
 * 🚨 TECHO DURO de la renovación entera (todos los intentos, esperas incluidas).
 *
 * No es una preferencia de UX: es el acoplamiento con la **ventana de gracia**
 * de Identidad (`REFRESH_GRACE_SECONDS`, **30 s** por defecto). El canje del
 * refresh rota la cookie en el servidor **aunque el navegador no llegue a
 * recibir la respuesta** (un intento abortado por `tiempoLimiteMs` es
 * exactamente eso). A partir de ahí el navegador conserva el id anterior, que
 * solo sigue siendo aceptable **mientras dure la gracia**; presentado después,
 * Identidad lo lee como **token reaparecido = robo** y **revoca la sesión
 * entera** — no solo la de esta web (`modelo-de-auth`, «La rotación del
 * refresh»).
 *
 * Consecuencia práctica para quien toque `POLITICA_RED`: **subir
 * `tiempoLimiteMs` o `intentos` sin subir la gracia del servidor empeora los
 * deslogueos en vez de mejorarlos.**
 */
export const PRESUPUESTO_TOTAL_MS = 25_000

// Códigos GraphQL que NO son un rechazo de la sesión: el servidor tropezó o
// hablamos idiomas distintos. Se tratan como `red` (reintentables, sesión
// intacta) porque sacar al usuario por un 500 del gateway —o por un despliegue
// a medias— es exactamente el defecto que este módulo viene a cerrar.
// El rechazo real del refresh llega como NOT_FOUND (goal sesion-viva-02) con
// HTTP 200: por eso la clasificación mira el CÓDIGO, no el estado HTTP.
const CODIGOS_QUE_NO_SON_SESION = new Set([
  'INTERNAL_SERVER_ERROR',
  'SERVICE_UNAVAILABLE',
  'RATE_LIMITED',
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'BAD_REQUEST',
  // `FORBIDDEN` es AUTORIZACIÓN, nunca sesión (raíz §5.6 y `modelo-de-auth`,
  // «Detección de sesión»), y el gateway lo emite por su cuenta al traducir un
  // `PERMISSION_DENIED` de gRPC. Faltaba aquí: por este camino un permiso
  // denegado cerraba la sesión, que es justo lo que el contrato prohíbe.
  'FORBIDDEN',
  'PERMISSION_DENIED',
])

const MUTATION = 'mutation refreshToken { refreshToken { accessToken } }'

interface CuerpoRefresh {
  data?: { refreshToken?: { accessToken?: string } | null } | null
  errors?: Array<{ message?: string; extensions?: { code?: string } }>
}

/**
 * URL del gateway resuelta EN CADA LLAMADA, no al importar el módulo: el
 * cliente Apollo sí lanza en el import si falta la env, y ese es justamente el
 * motivo por el que la adopción de sesión tenía que importarlo en diferido.
 * Aquí un entorno mal configurado sale como desenlace `red`, no como un módulo
 * que revienta al cargar (y el CI, sin `VITE_GATEWAY_URL`, no se cae).
 */
function urlDelGateway(): string {
  const url = getEnv('VITE_GATEWAY_URL')
  if (!url) {
    throw new Error(
      'VITE_GATEWAY_URL no configurada. Configura la variable en Rancher o en .env.local.',
    )
  }
  return url
}

// Single-flight: N operaciones fallando a la vez (me + facturas + negocios…)
// deben producir UNA sola renovación — el refresh es de un solo uso (rotación) y
// dos llamadas concurrentes con la misma cookie se pisarían. La cola cubre
// TAMBIÉN la ventana de los reintentos: mientras se espera el 2º intento, quien
// llegue se cuelga de esta misma promesa.
let enVuelo: Promise<DesenlaceRenovacion> | null = null

export function renovarAccessToken(): Promise<DesenlaceRenovacion> {
  enVuelo ??= conReintentos().finally(() => {
    enVuelo = null
  })
  return enVuelo
}

async function conReintentos(): Promise<DesenlaceRenovacion> {
  let motivo = 'sin respuesta del gateway'
  const arranque = Date.now()
  const restante = () => PRESUPUESTO_TOTAL_MS - (Date.now() - arranque)
  for (let intento = 1; intento <= POLITICA_RED.intentos; intento++) {
    // Fuera del presupuesto no se vuelve a llamar: un canje presentado después
    // de la ventana de gracia no es un reintento tardío, es una revocación de
    // la sesión completa (ver `PRESUPUESTO_TOTAL_MS`).
    const margen = Math.min(POLITICA_RED.tiempoLimiteMs, restante())
    if (margen <= 0) break
    const desenlace = await unIntento(margen)
    if (desenlace.tipo === 'renovado') {
      // Se guarda AQUÍ, en el único sitio que sabe que el token es fresco: así
      // ningún llamador puede renovar y olvidarse de almacenarlo.
      tokenStore.set(desenlace.token)
      return desenlace
    }
    if (desenlace.tipo === 'sesion-invalida') return desenlace
    motivo = desenlace.motivo
    if (intento < POLITICA_RED.intentos) {
      const espera = esperaTras(intento)
      if (espera >= restante()) break
      await esperar(espera)
    }
  }
  // Agotados los intentos —o el presupuesto— el desenlace SIGUE siendo `red`:
  // la sesión no se cierra, el usuario reintenta su acción.
  return { tipo: 'red', intentos: POLITICA_RED.intentos, motivo }
}

// `fetch` SIN Bearer (el vencido no sirve y no debe recursar en el reintento de
// sesión) y con `credentials: 'include'` — la sesión viaja en la cookie
// httpOnly. Nunca lanza: clasifica.
async function unIntento(tiempoLimiteMs: number): Promise<DesenlaceIntento> {
  const cortar = new AbortController()
  const reloj = setTimeout(() => cortar.abort(), tiempoLimiteMs)
  // 🚨 El reloj se apaga cuando termina el intento ENTERO, cabeceras **y**
  // cuerpo. Apagarlo en cuanto `fetch` resuelve deja la lectura del cuerpo sin
  // techo, y un `HTTP 200` cuyo JSON no llega nunca cuelga la promesa del
  // single-flight **para siempre**: nadie recibe desenlace y no hay reintentos.
  try {
    let res: Response
    try {
      res = await fetch(urlDelGateway(), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: MUTATION, operationName: 'refreshToken' }),
        signal: cortar.signal,
      })
    } catch (e) {
      return { tipo: 'red', motivo: descripcion(e) }
    }

    // Un 4xx/5xx de TRANSPORTE no es una sesión muerta (502 de nginx, 504 del
    // proxy, 400 por un esquema desfasado…). Solo la mutation respondiendo «no
    // puedo renovarte» lo es.
    if (!res.ok) {
      return { tipo: 'red', motivo: `el gateway respondió ${res.status}` }
    }

    let cuerpo: CuerpoRefresh | null
    try {
      cuerpo = (await res.json()) as CuerpoRefresh | null
    } catch (e) {
      // Aquí cae también el corte del reloj: abortar tras las cabeceras corta
      // el flujo del cuerpo y `json()` rechaza.
      return { tipo: 'red', motivo: descripcion(e) }
    }
    if (!cuerpo) return { tipo: 'red', motivo: 'respuesta ilegible del gateway' }

    const error = cuerpo.errors?.[0]
    if (error) {
      const codigo = error.extensions?.code ?? ''
      const motivo = `${codigo || 'sin código'}: ${error.message ?? 'error del gateway'}`
      return CODIGOS_QUE_NO_SON_SESION.has(codigo)
        ? { tipo: 'red', motivo }
        : { tipo: 'sesion-invalida', motivo }
    }

    const token = cuerpo.data?.refreshToken?.accessToken
    if (!token) {
      return { tipo: 'sesion-invalida', motivo: 'la renovación no devolvió accessToken' }
    }
    return { tipo: 'renovado', token }
  } finally {
    clearTimeout(reloj)
  }
}

/** Espera creciente con variación aleatoria, con techo. */
export function esperaTras(intento: number): number {
  const base = POLITICA_RED.esperaBaseMs * POLITICA_RED.factor ** (intento - 1)
  const techo = Math.min(base, POLITICA_RED.esperaMaximaMs)
  const desvio = (Math.random() * 2 - 1) * POLITICA_RED.variacion
  return Math.max(0, Math.round(techo * (1 + desvio)))
}

function esperar(ms: number): Promise<void> {
  return new Promise((resolver) => setTimeout(resolver, ms))
}

function descripcion(e: unknown): string {
  if (e instanceof Error) {
    return e.name === 'AbortError' ? 'la renovación agotó su tiempo límite' : e.message
  }
  return 'fallo de red'
}
